import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InfoHint from '../atoms/InfoHint';
import { IconDollarSign } from '../atoms/Icon';

/**
 * As quatro formas, na ordem em que aparecem no cardápio do cliente.
 *
 * `exigeEquipamento` é o que separa o padrão seguro do resto: entrega e retirada
 * um restaurante faz sem comprar nada, mas aceitar cartão exige maquininha —
 * então cartão nunca entra por omissão.
 */
export const FORMAS = [
  { id: 'pix', label: 'Pix', hint: 'Transferência na hora' },
  { id: 'cash', label: 'Dinheiro', hint: 'Na entrega ou no balcão' },
  { id: 'credit', label: 'Cartão de crédito', hint: 'Na maquininha', exigeEquipamento: true },
  { id: 'debit', label: 'Cartão de débito', hint: 'Na maquininha', exigeEquipamento: true },
];

/** O que o cardápio usa quando o lojista nunca configurou. Espelha
 *  `PADRAO_SEM_CONFIGURACAO` do backend (services/payment_methods.py): as duas
 *  que não exigem equipamento. Aqui serve só para a tela poder dizer ao lojista
 *  o que está valendo enquanto ele não escolheu. */
const PADRAO = ['pix', 'cash'];

export function buildPayments(establishment) {
  const declaradas = establishment?.paymentMethods;
  return {
    // `null` do backend significa "nunca configurou", e é diferente de lista
    // vazia ("combino direto com o cliente"). A tela precisa dos dois estados
    // separados para mostrar o padrão como padrão, e não como escolha dele.
    configurado: Array.isArray(declaradas),
    methods: Array.isArray(declaradas) ? declaradas : PADRAO,
  };
}

export function samePayments(a, b) {
  if (a.configurado !== b.configurado) return false;
  return a.methods.length === b.methods.length && a.methods.every((m) => b.methods.includes(m));
}

/** Nada impede de salvar: lista vazia é escolha legítima ("combino no WhatsApp"),
 *  e o backend aceita. O aviso de consequência fica no corpo do formulário. */
export function paymentsError() {
  return null;
}

/** Manda a lista sempre, mesmo igual ao padrão: é o que transforma "nunca
 *  configurei" em "escolhi estas duas", e a partir daí uma mudança de padrão do
 *  sistema não muda o que este estabelecimento oferece. */
export function paymentsToPayload(payments) {
  return { paymentMethods: payments.methods };
}

function PaymentMethodsForm({ value, onChange, temChavePix }) {
  const marcada = (id) => value.methods.includes(id);

  function alternar(id) {
    const proximas = marcada(id)
      ? value.methods.filter((m) => m !== id)
      : [...value.methods, id];
    onChange({ configurado: true, methods: proximas });
  }

  // Pix marcado sem chave cadastrada não chega ao cliente: o backend o filtra da
  // lista pública, justamente para o cliente não escolher Pix e cair numa tela
  // sem chave. Sem este aviso o lojista marca, salva com sucesso e nunca entende
  // por que Pix não aparece no cardápio.
  const pixSemChave = marcada('pix') && !temChavePix;

  // Pode acontecer: marcou só Pix e não cadastrou chave. O cardápio manda
  // combinar pelo WhatsApp em vez de travar o pedido, mas o lojista precisa
  // saber que chegou nesse estado.
  const efetivas = value.methods.filter((m) => m !== 'pix' || temChavePix);

  return (
    <Card className="border-0 card-elevated" style={{ borderRadius: 'var(--radius-md)' }}>
      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <IconDollarSign size={16} style={{ color: 'var(--color-purple-primary)' }} />
          <h6 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>
            Formas de pagamento
          </h6>
        </div>
        <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
          O cliente só escolhe entre as marcadas aqui.
        </p>

        {FORMAS.map((f) => (
          <div key={f.id} className="d-flex align-items-center gap-2 py-2">
            <Form.Check
              type="switch"
              id={`forma-${f.id}`}
              checked={marcada(f.id)}
              onChange={() => alternar(f.id)}
              aria-label={f.label}
            />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{f.label}</div>
              <div style={{ fontSize: 11.5, color: '#999' }}>{f.hint}</div>
            </div>
          </div>
        ))}

        {/* Enquanto ele não mexeu em nada, o que vale é o padrão — e ele precisa
            saber que cartão NÃO está incluído nele, senão descobre pelo cliente
            que ligou perguntando se pode pagar na maquininha. */}
        {!value.configurado && (
          <InfoHint>
            Você ainda não escolheu: seu cardápio está oferecendo <strong>Pix e dinheiro</strong>.
            Cartão não entra sozinho, porque depende de você ter maquininha — marque acima se
            aceitar.
          </InfoHint>
        )}

        {pixSemChave && (
          <InfoHint>
            Pix está marcado, mas você não cadastrou a chave. Enquanto isso ele{' '}
            <strong>não aparece no cardápio</strong> — o cliente escolheria Pix e chegaria
            numa tela sem chave nenhuma. Cadastre a chave em "Recebimento por Pix".
          </InfoHint>
        )}

        {efetivas.length === 0 && (
          <InfoHint>
            Nenhuma forma chega ao cliente hoje. O cardápio vai pedir que ele combine o
            pagamento pelo WhatsApp — o pedido não é bloqueado, mas ninguém escolhe nada.
          </InfoHint>
        )}
      </Card.Body>
    </Card>
  );
}

export default PaymentMethodsForm;
